import { User } from "api/auth/types/user";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { fetchJson } from "shared/fetch/fetchJson";
import Loading from "shared/loading";
import { useAbortController } from "shared/useAbortController";
import { useRunOnMount } from "shared/useRunOnMount/useRunOnMount";

export interface UserContextType {
  user?: User;
  hasRole: (role: string) => boolean;
  isLoggedIn: () => boolean;
  setUser: (user: User) => void;
  clearUser: () => void;
  authHelpers: { hasRole: typeof authHelpers["hasRole"] };
}

const authHelpers = {
  hasRole(user: User | undefined, role: string) {
    if (!user) {
      return false;
    }
    return !!user?.roles.find((r) => r === role);
  },
};

const initialUserContext: UserContextType = {
  user: undefined,
  hasRole: (role) => false,
  isLoggedIn: () => false,
  setUser: (user) => {},
  clearUser: () => {},
  authHelpers,
};
const UserContextValue = createContext<UserContextType>(initialUserContext);

export function UserContainer({ children }) {
  const [user, setUserState] = useState<User | undefined>(undefined);
  const [isLoaded, setIsLoaded] = useState(false);
  const abortController = useAbortController();
  useRunOnMount(() => {
    async function load() {
      try {
        const { data } = await fetchJson<User>("/auth/api/tryGetUser", {
          signal: abortController.signal,
        });
        setUserState(data);
        setIsLoaded(true);
      } catch (e) {
        if (!abortController.signal.aborted) setIsLoaded(true);
      }
    }

    load();
  });

  const hasRole = useCallback(
    (role: string) => {
      return authHelpers.hasRole(user, role);
    },
    [user]
  );

  const isLoggedIn = useCallback(() => {
    return !!user;
  }, [user]);

  const setUser = useCallback((user: User) => {
    setUserState(user);
  }, []);
  const clearUser = useCallback(() => {
    setUserState(undefined);
  }, []);

  const userState = useMemo(() => {
    return { user, hasRole, isLoggedIn, setUser, clearUser, authHelpers };
  }, [clearUser, hasRole, isLoggedIn, setUser, user]);

  return (
    <UserContextValue.Provider value={userState}>
      {!isLoaded ? (
        <div className="vh-100 d-flex align-items-center justify-content-center">
          <Loading />
        </div>
      ) : (
        children
      )}
    </UserContextValue.Provider>
  );
}

export function useUser() {
  const context = useContext<UserContextType>(UserContextValue);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserContextContainer");
  }
  return context;
}
